import { type Href, useRouter } from "expo-router";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import type { DisplayEvent } from "@/lib/event-helpers";

import { sessionAtom } from "@/atoms/session";
import { EventCard } from "@/components/EventCard";
import { GText } from "@/components/g-text";
import SignInModal from "@/components/sign-in/sign-in-modal";
import { colors } from "@/constants/colors";
import { fetchAllEvents } from "@/lib/event-queries";
import { generateUniqueSlug } from "@/lib/slug";
import { supabase } from "@/lib/supabase";

export default function HomeScreen() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const [events, setEvents] = useState<DisplayEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const sessionValue = useAtomValue(sessionAtom);
  const authLoading = sessionValue === "loading";
  const session = sessionValue === "loading" ? null : sessionValue;
  const [signInVisible, setSignInVisible] = useState(false);

  useEffect(() => {
    fetchAllEvents()
      .then(setEvents)
      .finally(() => setLoading(false));
  }, []);

  return (
    <View style={styles.container}>
      <View style={[styles.header, { paddingTop: insets.top + 16 }]}>
        <GText style={styles.heading}>upcoming events</GText>
        {session && (
          <Pressable
            onPress={() => supabase.auth.signOut()}
            style={styles.logoutButton}
          >
            <GText style={styles.logoutText}>logout</GText>
          </Pressable>
        )}
      </View>

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={colors.white} size="large" />
        </View>
      ) : events.length === 0 ? (
        <View style={styles.center}>
          <GText style={styles.emptyText}>No events yet</GText>
        </View>
      ) : (
        <FlatList
          contentContainerStyle={[
            styles.list,
            styles.listContent,
            {
              maxWidth: 600,
              paddingBottom: insets.bottom + 80,
            },
          ]}
          data={events}
          keyExtractor={(e) => e.id}
          renderItem={({ item }) => (
            <EventCard event={item} showStatus={item.isAdmin} />
          )}
          showsVerticalScrollIndicator={false}
        />
      )}

      {!authLoading && (
        <View
          style={[
            styles.createContainer,
            { paddingBottom: insets.bottom + 12 },
          ]}
        >
          {session ? (
            <Pressable
              onPress={async () => {
                const timezone =
                  Intl.DateTimeFormat().resolvedOptions().timeZone;
                const slug = await generateUniqueSlug();
                const { data, error } = await supabase
                  .from("event")
                  .insert({
                    created_by_user_id: session?.user.id,
                    slug,
                    timezone,
                  })
                  .select("slug")
                  .single();

                console.log("direct insert result:", { data, error });
                if (!data) return;
                router.push(`/e/${data.slug}?mode=edit` as Href);
              }}
              style={styles.createButton}
            >
              <GText style={styles.createText}>+ create event</GText>
            </Pressable>
          ) : (
            <Pressable
              onPress={() => setSignInVisible(true)}
              style={styles.createButton}
            >
              <GText style={styles.createText}>sign in</GText>
            </Pressable>
          )}
        </View>
      )}

      <SignInModal
        onClose={() => setSignInVisible(false)}
        onSignedIn={() => setSignInVisible(false)}
        visible={signInVisible}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  center: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
  },
  container: {
    backgroundColor: colors.transparent,
    flex: 1,
  },
  createButton: {
    backgroundColor: colors.white,
    borderRadius: 28,
    paddingHorizontal: 28,
    paddingVertical: 14,
  },
  createContainer: {
    alignItems: "center",
    backgroundColor: colors.transparent,
    bottom: 0,
    left: 0,
    paddingTop: 12,
    position: "absolute",
    right: 0,
  },
  createText: {
    color: colors.black,
    fontSize: 16,
    fontWeight: "700",
  },
  emptyText: {
    color: colors.light,
    fontSize: 16,
  },
  header: {
    alignItems: "center",
    alignSelf: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: 16,
    paddingHorizontal: 24,
    width: "100%",
  },
  heading: {
    color: colors.light,
    fontSize: 32,
    fontWeight: "800",
  },
  list: {
    paddingHorizontal: 24,
  },
  listContent: {
    alignSelf: "center",
    width: "100%",
  },
  logoutButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  logoutText: {
    color: colors.light,
    fontSize: 14,
  },
});
